<template>
  <v-layout row wrap class="base-card-tabbed">
    <base-card
      v-bind="{
        ...$attrs,
        moduleIcon: $props.moduleIcon ? $props.moduleIcon : cardHeaderIcon,
        ...$props,
      }"
      v-on="$listeners"
    >
      <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
        <slot :name="name" v-bind="data"></slot>
      </template>
      <template #table>
        <slot name="shared-card-contents" />
        <div v-if="tabs.length === 0" key="no-tabs">
          <slot name="no-tabs">
            <p class="ma-0 px-3 py-2">No content provided</p>
          </slot>
        </div>
        <base-tabs
          v-else
          key="has-tabs"
          v-model="activeTab"
          :tabs="tabs"
          :has-titles="hasTitles"
          :show-arrows="showArrows"
          @input="$emit('tab-change')"
        >
          <template #title="{ tab }">
            <!-- Check if these fall back to the BaseTabs defaults -->
            <slot name="tab-title" :tab="tab" />
          </template>
          <template #content="{ tab }">
            <slot name="tab-content" :tab="tab" />
          </template>
          <template #action>
            <slot name="tab-action" />
          </template>
        </base-tabs>
      </template>
    </base-card>
  </v-layout>
</template>

<script>
/**
 * This component displays a card with a title and a body of tabbed content. The
 * title, tab titles, and tab contents can all be provided through slots. A
 * loading bar and error display can also be provided through the props
 */
import BaseTabs from './BaseTabs.vue';
import BaseCard from './BaseCard.vue';

export default {
  name: 'BaseCardTabbed',
  components: {
    BaseTabs,
    BaseCard,
  },
  props: {
    // v-model for active tab
    value: {
      type: Number,
      required: true,
    },
    cardHeaderIcon: {
      type: String,
      default() {
        return '';
      },
    },
    panelAlwaysOpen: {
      type: Boolean,
    },
    tabs: {
      type: Array,
      required: true,
      // validation occurs in BaseTabs
    },
    loading: {
      type: Boolean,
    },
    errorMessage: {
      type: String,
      default() {
        return undefined;
      },
    },
    titleHidden: {
      type: Boolean,
    },
    hasTitles: {
      type: Boolean,
      default: true,
    },
    showArrows: {
      type: Boolean,
    },
    displayOrder: {
      type: Number,
      default: undefined,
    },
    title: {
      type: String,
      required: true,
    },
    /**
     * An identifier for the card, this is used to create IDs for selectors, and
     * also passed to the "pin/unpin" component as the card name
     */
    idTag: {
      type: String,
      default() {
        return undefined;
      },
    },
    userGuideIdentifier: {
      type: Object,
      default: () => ({}),
    },
    hidePinButton: {
      type: Boolean,
    },
    /**
     * Parameters to store in the DB with the pinned card, if any are needed to
     * save some context for rendering on the dashboard
     */
    pinParameters: {
      type: Object,
      default: () => ({}),
    },
    showCollapseButton: {
      type: Boolean,
    },
    showCloseButton: {
      type: Boolean,
    },
    apiEndpoints: {
      type: Array,
      required: true,
      validator(arr) {
        return arr.filter((item) => typeof item !== 'string').length === 0;
      },
    },
  },
  computed: {
    activeTab: {
      get() {
        return this.value;
      },
      set(newValue) {
        // v-model for active tab
        this.$emit('input', newValue);
      },
    },
  },
};
</script>
<style>
.base-card-tabbed .sb_component_card_body .v-card__text {
  padding: 0 0 16px 0;
}
</style>
